import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Trans } from 'react-i18next';
import { updateCommonStoreFromEventsWsData } from '~shared/components/react/CommonDataStore/actions';
import { onWsEventUpdate } from '~shared/services/mainSocket';
import {
    startListeningForUpdates,
    stopListeningForUpdates,
} from '~shared/components/react/BetCardClassic/utils';
import {
    WS_HANDLER_COMPONENT_NAME,
    I18N_MADE_FOR_YOU_NO_RECOMMENDED_TEXT,
} from '~scenes/madeforyou/constants';
import {
    selectActiveTab,
    selectBetCards,
    selectCommonDataEvents,
    selectIsMadeForYouWidget,
    selectTabIsLoading,
    selectBetCardsByPage,
    selectCurrentPage,
} from '~scenes/madeforyou/app/selectors/mfySelectors';
import { useLazyLoadContext } from '~spa/Components/LazyLoad/LazyLoad';

import BetCardSimple from '~spa/Components/BetCardSimple';
import BetCardMulti from '~spa/Components/BetCardSimple/BetCardMulti';
import { getSelectionsData } from '~spa/Components/BetCardSimple/utils';
import { getBetCards } from '~scenes/madeforyou/service';
import NoResults from '~shared/ui/NoResults';
import { getGtmPrefix } from '~scenes/madeforyou/utils';
import {
    decrementCurrentPage,
 } from '~scenes/madeforyou/app/actions/mfyActions';
import {
    useIsUserLoggedIn,
} from '~SpectateStore/hooks/userHooks';

/**
 * @param  {*} {betCards}
 * @returns {React.Component} React component collection
 */
const BetCards = () => {
    const dispatch = useDispatch();
    const isUserLoggedIn = useIsUserLoggedIn();
    const lazyLoadContext = useLazyLoadContext();
    const activeTab = useSelector(selectActiveTab);
    const betCards = useSelector(selectBetCards);
    const currentPage = useSelector(selectCurrentPage);
    const currentPageBetCards = useSelector(selectBetCardsByPage);
    const isWidget = useSelector(selectIsMadeForYouWidget);
    const isLoading = useSelector(selectTabIsLoading);
    const commonDataEvents = useSelector(selectCommonDataEvents);
    const gtmAttribute = getGtmPrefix(activeTab, isWidget);
    const betCardsToRender = isWidget ? currentPageBetCards : betCards;
    const hasNoCards = betCards.length === 0;
    const hasNoCardsOnPage = betCardsToRender.length === 0;

    useEffect(() => {
        startListeningForUpdates(betCards, WS_HANDLER_COMPONENT_NAME);
        const unsubscribe = onWsEventUpdate((wsData) => {
            dispatch(updateCommonStoreFromEventsWsData(wsData));
        });

        return () => {
            stopListeningForUpdates(betCards, WS_HANDLER_COMPONENT_NAME);
            unsubscribe();
        };
    }, [betCards]);

    useEffect(() => {
        if (hasNoCardsOnPage && currentPage > 1) {
            dispatch(decrementCurrentPage());
        }
    }, [hasNoCardsOnPage, currentPage]);

    useEffect(() => {
        // Make request for tab inforamtion if necesary and handles response.
        if(!isLoading) {
            getBetCards(activeTab, isWidget, isUserLoggedIn, lazyLoadContext, dispatch);
        }
    }, [activeTab]);

    if (isLoading) {
        return null;
    }

    if (hasNoCards) {
        return <NoResults>
            <Trans i18nKey={I18N_MADE_FOR_YOU_NO_RECOMMENDED_TEXT} />
        </NoResults>;
    }

    return betCardsToRender.map(betcard => {
        const selections = getSelectionsData(betcard, commonDataEvents, gtmAttribute);
        if (selections.length === 0) {
            return null;
        }

        if (betcard.bet_type !== 'Singles') {
            return <div
                key={betcard.betcard_id}
                id={`${activeTab}_js-bb-betcard-${betcard.betcard_id}`}
                className="bb-betcard">
                    <BetCardMulti
                        key={betcard.betcard_id}
                        selections={selections}
                        betcard={betcard}
                        gaComponent="betfeed-widget"
                        cssClass="simple-betcard--no-margin"
                    />
            </div>;
        }

        return (
            <div
                key={betcard.betcard_id}
                id={`${activeTab}_js-bb-betcard-${betcard.betcard_id}`}
                className="bb-betcard">
                <BetCardSimple
                    key={betcard.betcard_id}
                    {...selections.shift()}
                    cssClass="simple-betcard--no-margin"
                />
            </div>
        );
    });
};

export default BetCards;
